import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";

import "../../public/ikzoekbaas.css";
import heroKonijnen from "../../public/beeld/hero-konijnen.webp";
import {
  KnopEnvelop,
  KnopPijl,
  DeelIcoon,
  SliderPijl,
  StapChevron,
  StapIcoon,
  TaartIcoon,
} from "../../public/ikzoekbaasIconen";
import { MerkLogoBreed, MerkLogoVierkant } from "../../public/ikzoekbaasMerk";
import {
  FilterChevron,
  GeslachtIcoon,
  MenuIcoon,
  PinIcoon,
  SoortGlyph,
  TegelPijl,
} from "../../public/ikzoekbaasSoortIconen";
import { kenmerkLabel } from "../../public/kenmerkLabels";
import { useTelWeergave } from "../../public/weergaveTellen";
import {
  fetchPublication,
  fetchOpvangen,
  fetchSearch,
  fetchSuccesverhalen,
  searchParams,
  statusOf,
  type Facet,
  type PublicAnimalDetail,
  type PublicOpvang,
  type PublicSearchResult,
  type PublicSuccesverhaal,
  type SearchFilters,
  type Sortering,
} from "../../public/publicApi";
import {
  groepLabel,
  pathFor,
  routeFromPath,
  siteBase,
  SOORT_GROEPEN,
  type Route,
} from "../../public/ikzoekbaasRoute";

// The public ikzoekbaas.nl adoption site (epic-8). A standalone, unauthenticated surface that
// bypasses the auth Gate, rendering the platform's `live` publications on the IKZOEKBAAS channel:
//
//   /                              the home page — category tiles with live counts, newest animals
//   /zoek-asieldieren/<groep>      the faceted search: sidebar filters with counts, sort, paging
//   /asieldier/<groep>/<slug>      the advertisement page
//
// Navigation is client-side (history pushState) so deep links resolve and the back button works;
// both entrances (own host, /ikzoekbaas path) share every component and differ only in base path.
//
// The search filters live in the URL, not just in state: a filtered search is something a visitor
// bookmarks and sends to someone else, and a page that forgets its filters on reload is not a search
// page. That is also why the sidebar renders whatever facets the API returns rather than a hard-coded
// list — the questions are data, and the shelters decide which ones they answer.
//
// Every block is conditional on its data. A publication that predates the epic-8 contract still
// renders a complete, readable page: it simply shows fewer blocks, never an empty frame or "null".

const PAGE_SIZE = 15;

/**
 * De afstanden die het keuzelijstje aanbiedt, in kilometer. Dezelfde reeks als de echte site.
 *
 * <p>Vier stappen en niet twintig: de bezoeker kiest een orde van grootte ("in de buurt", "een
 * ritje", "heel Nederland"), geen precieze straal. Een schuifregelaar van 1 tot 100 suggereert een
 * nauwkeurigheid die de vogelvluchtafstand toch niet waarmaakt.
 */
const STRALEN = [5, 10, 25, 50] as const;

/**
 * De vier manieren waarop hun site een dier laat delen, met dezelfde bestemmingen.
 *
 * <p>De URL die gedeeld wordt is de pagina waar de bezoeker op staat en niet iets wat hier in elkaar
 * gezet wordt: dit draait op twee ingangen (eigen host en `/ikzoekbaas`), en een zelfgebouwde link
 * klopt dan op één van de twee.
 */
const DELEN = [
  {
    merk: "facebook" as const,
    label: "Deel op Facebook",
    url: (u: string) => `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(u)}`,
  },
  {
    merk: "x" as const,
    label: "Deel op X",
    url: (u: string, naam: string) =>
      `https://twitter.com/intent/tweet?text=${encodeURIComponent(
        `Hallo, ik wil graag dit dierenprofiel van ${naam} met je delen: ${u}`,
      )}`,
  },
  {
    merk: "mail" as const,
    label: "Deel per e-mail",
    url: (u: string, naam: string) =>
      `mailto:?subject=${encodeURIComponent("Dierenprofiel")}&body=${encodeURIComponent(
        `Hallo, ik wil graag dit dierenprofiel van ${naam} met je delen: ${u}`,
      )}`,
  },
  {
    merk: "whatsapp" as const,
    label: "Deel via WhatsApp",
    url: (u: string, naam: string) =>
      `https://wa.me/?text=${encodeURIComponent(
        `Hallo, ik wil graag dit dierenprofiel van ${naam} met je delen: ${u}`,
      )}`,
  },
] as const;
const KANAAL = "IKZOEKBAAS" as const;

/**
 * Where the donate block sends people: the Dierenbescherming's own permanent donation page — the
 * same destination their header button uses.
 *
 * <p>Deliberately NOT the seasonal campaign their home page links to today
 * ({@code /zomerdrukte-2026}). That URL and its copy turn over with the season, and a campaign we
 * cannot see the end of would sit here advertising last summer. The permanent page always works.
 */
const DONEER_URL = "https://steun.dierenbescherming.nl/doneer";

/** The campaign the donate block describes. Reachable from "lees meer", never from the button. */
const ZOMERDRUKTE_URL = "https://www.dierenbescherming.nl/werkzaamheden/acties/zomerdrukte";

/** The Dier & Mens Wijzer and the tips page it sits next to — both theirs, both permanent. */
const WIJZER_URL = "https://diermenswijzer.dierenbescherming.nl/";
const TIPS_URL = "https://www.dierenbescherming.nl/diervriendelijk-leven/zorgen-voor-dieren";

/** Their footer's own links, verbatim. The animal columns stay inside this site; the rest do not. */
const VOET_STEUN: ReadonlyArray<readonly [string, string]> = [
  ["Steun een asiel", "https://www.dierenbescherming.nl/werkzaamheden/dierennoodhulp/steun-het-asiel"],
  ["Steun landelijk", "https://steun.dierenbescherming.nl/doneer"],
  ["Start een actie", "https://www.voordierenbescherming.nl/locaties/actie-starten"],
  ["Bekijk alle opties", "https://www.dierenbescherming.nl/steun-financieel"],
];
const VOET_CONTACT: ReadonlyArray<readonly [string, string]> = [
  ["Belangrijke nummers", "https://www.dierenbescherming.nl/contact"],
  ["Onze locaties", "https://www.dierenbescherming.nl/onze-locaties"],
  ["Meld je aan voor de nieuwsbrief", "https://steun.dierenbescherming.nl/nieuwsbrief"],
];

/** A play triangle for the wijzer frame. */
function SpeelPijl() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M9 6.5v11l9-5.5-9-5.5Z" />
    </svg>
  );
}

/** A soft paw glyph for photo placeholders (no external asset — inline, tintable via currentColor). */
function PawGlyph() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M12 14.5c-2.3 0-4.2 1.6-4.2 3.6 0 1.3 1 2.1 2.4 2.1.7 0 1.2-.2 1.8-.4.6.2 1.1.4 1.8.4 1.4 0 2.4-.8 2.4-2.1 0-2-1.9-3.6-4.2-3.6Z" />
      <ellipse cx="6.5" cy="11" rx="1.7" ry="2.2" />
      <ellipse cx="17.5" cy="11" rx="1.7" ry="2.2" />
      <ellipse cx="9.5" cy="7.5" rx="1.6" ry="2.1" />
      <ellipse cx="14.5" cy="7.5" rx="1.6" ry="2.1" />
    </svg>
  );
}

/** A photo that falls back to a paw placeholder when the src is absent or fails to load. */
function Photo({ src, alt }: { src: string | null; alt: string }) {
  const [failed, setFailed] = useState(false);
  if (!src || failed) {
    return (
      <span className="izb__leegfoto">
        <PawGlyph />
      </span>
    );
  }
  return <img src={src} alt={alt} loading="lazy" onError={() => setFailed(true)} />;
}

const MAANDEN = [
  "januari", "februari", "maart", "april", "mei", "juni",
  "juli", "augustus", "september", "oktober", "november", "december",
];

/** An ISO date as "24 december 2024" — that is how the birth date reads on the site. */
export function nlDatum(iso: string | null | undefined): string | null {
  if (!iso) {
    return null;
  }
  const match = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) {
    return null;
  }
  const maand = MAANDEN[Number(match[2]) - 1];
  return maand ? `${Number(match[3])} ${maand} ${match[1]}` : null;
}

// ── shell ────────────────────────────────────────────────────────────────────────────────────────

export function IkZoekBaasSite() {
  // The entrance is fixed for the visit: resolved once, so a pushState never changes which base the
  // links hang off.
  const base = useMemo(
    () => siteBase(globalThis.location?.hostname, globalThis.location?.pathname),
    [],
  );
  const [route, setRoute] = useState<Route>(() =>
    routeFromPath(globalThis.location?.pathname ?? "/"),
  );

  useEffect(() => {
    const onPop = () => setRoute(routeFromPath(globalThis.location?.pathname ?? "/"));
    globalThis.addEventListener?.("popstate", onPop);
    return () => globalThis.removeEventListener?.("popstate", onPop);
  }, []);

  const navigate = useCallback(
    (next: Route, query?: URLSearchParams) => {
      const qs = query?.toString();
      globalThis.history?.pushState?.(null, "", pathFor(base, next) + (qs ? `?${qs}` : ""));
      setRoute(next);
      globalThis.scrollTo?.(0, 0);
    },
    [base],
  );

  /**
   * De kale stand: geen kop, geen voettekst, geen navigatie — alleen het aanbod. Dat is wat er in
   * een `iframe` op de website van een asiel past; met onze kop erboven staan er twee koppen op
   * hun pagina en leest het als een site-in-een-site.
   *
   * <p>Uit de URL en niet uit een instelling, zodat het asiel zelf bepaalt hoe het insluit zonder
   * dat wij per opvang iets hoeven te zetten. Alleen op de opvangpagina: de rest van de site heeft
   * er niets te zoeken en hoort niet inkaderbaar te zijn.
   */
  const insluiten =
    route.view === "opvang"
    && new URLSearchParams(globalThis.location?.search ?? "").get("insluiten") === "1";

  const [menuOpen, setMenuOpen] = useState(false);
  const goHome = useCallback(() => {
    setMenuOpen(false);
    navigate({ view: "home" });
  }, [navigate]);
  const openZoek = useCallback(
    (groep: string) => {
      setMenuOpen(false);
      navigate({ view: "zoek", groep });
    },
    [navigate],
  );

  return (
    <div className={insluiten ? "izb izb--ingesloten" : "izb"}>
      {/* Two rows, as on the real site: the logo spans both, a thin service row sits above the
          category nav, and the two pills hold the right edge. On a phone the nav folds into the
          hamburger and the service row disappears — it is the first thing that has to go. */}
      {!insluiten && (
      <header className="izb__nav">
        {/* Both cuts ship; CSS shows whichever fits, exactly as the site does. */}
        <button type="button" className="izb__logo" onClick={goHome} aria-label="Naar de homepagina">
          <MerkLogoBreed />
          <MerkLogoVierkant />
        </button>

        <div className="izb__nav-kolom">
          <div className="izb__nav-service">
            <span>Asieldieren van de aangesloten opvangcentra</span>
          </div>
          <nav className={menuOpen ? "izb__menu is-open" : "izb__menu"} aria-label="Hoofdmenu">
            {SOORT_GROEPEN.map((g) => (
              <button
                key={g.pad}
                type="button"
                onClick={() => openZoek(g.pad)}
                aria-current={route.view === "zoek" && route.groep === g.pad ? "page" : undefined}
              >
                {g.label}
              </button>
            ))}
          </nav>
        </div>

        <span className="izb__nav-acties">
          <a
            className="izb__pil izb__doneerpil"
            href={DONEER_URL}
            target="_blank"
            rel="noreferrer noopener"
          >
            Doneer
          </a>
          <button type="button" className="izb__pil izb__pil--zwart" onClick={() => openZoek("")}>
            Zoek een dier
          </button>
          {/* The category strip does not fit on a phone; it folds into this. */}
          <button
            type="button"
            className="izb__pil izb__pil--zwart izb__menuknop"
            aria-expanded={menuOpen}
            aria-label={menuOpen ? "Sluit menu" : "Open menu"}
            onClick={() => setMenuOpen((o) => !o)}
          >
            <MenuIcoon open={menuOpen} />
          </button>
        </span>
      </header>
      )}

      {route.view === "home" && (
        <Home
          onZoek={openZoek}
          onOpen={(groep, slug) => navigate({ view: "detail", groep, slug })}
        />
      )}
      {route.view === "zoek" && (
        <Zoek
          groep={route.groep}
          onOpen={(groep, slug) => navigate({ view: "detail", groep, slug })}
          onGroep={openZoek}
          onQuery={(query) => {
            const qs = query.toString();
            globalThis.history?.replaceState?.(
              null,
              "",
              pathFor(base, route) + (qs ? `?${qs}` : ""),
            );
          }}
        />
      )}
      {route.view === "detail" && (
        <Detail
          slug={route.slug}
          groep={route.groep}
          onBack={() => openZoek(route.groep)}
          onHome={() => navigate({ view: "home" })}
        />
      )}
      {route.view === "opvang" && (
        <OpvangPagina
          sleutel={route.sleutel}
          insluiten={insluiten}
          onOpen={(groep, slug) => navigate({ view: "detail", groep, slug })}
          onGroep={openZoek}
          onQuery={(query) => {
            const qs = query.toString();
            globalThis.history?.replaceState?.(
              null,
              "",
              globalThis.location.pathname + (qs ? `?${qs}` : ""),
            );
          }}
        />
      )}

      {/* Their footer: four columns over a mint service bar. The "zoek een dier" column is the only
          one that stays on this site — the other three are the Dierenbescherming's own pages, and
          linking out to the real ones beats inventing pages this platform does not serve.

          Their Account column is left out on purpose: it points at logging in and registering, and
          this platform has no visitor accounts. Three links to a door that is not there is worse
          than a column fewer. Their partner marks (Postcode Loterij, ANBI, CBF) are missing for the
          same kind of reason — those are certifications, and showing one we cannot verify is a
          claim, not decoration. */}
      {!insluiten && (
      <footer className="izb__voet">
        <div className="izb__voet-kolommen">
          <div className="izb__voet-kolom">
            <h2>Zoek een dier</h2>
            <ul>
              {SOORT_GROEPEN.map((g) => (
                <li key={g.pad}>
                  <button type="button" onClick={() => openZoek(g.pad)}>
                    {g.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>
          <div className="izb__voet-kolom">
            <h2>Steun ons</h2>
            <ul>
              {VOET_STEUN.map(([tekst, url]) => (
                <li key={url}>
                  <a href={url} target="_blank" rel="noreferrer noopener">
                    {tekst}
                  </a>
                </li>
              ))}
            </ul>
          </div>
          <div className="izb__voet-kolom">
            <h2>Contact</h2>
            <ul>
              {VOET_CONTACT.map(([tekst, url]) => (
                <li key={url}>
                  <a href={url} target="_blank" rel="noreferrer noopener">
                    {tekst}
                  </a>
                </li>
              ))}
            </ul>
          </div>
          <div className="izb__voet-kolom">
            <h2>Over deze site</h2>
            <p>Alle dieren op deze pagina worden aangeboden door aangesloten asielen.</p>
          </div>
        </div>
        <div className="izb__voet-balk">
          <span>Copyright © {new Date().getFullYear()}</span>
          <a
            href="https://www.dierenbescherming.nl/privacyverklaring"
            target="_blank"
            rel="noreferrer noopener"
          >
            Privacyverklaring
          </a>
        </div>
      </footer>
      )}
    </div>
  );
}

// ── home ─────────────────────────────────────────────────────────────────────────────────────────

function Home({ onZoek, onOpen }: {
  onZoek: (groep: string) => void;
  onOpen: (groep: string, slug: string) => void;
}) {
  const [result, setResult] = useState<PublicSearchResult | null>(null);
  const [buitenbeentjes, setBuitenbeentjes] = useState<PublicSearchResult | null>(null);
  const [langst, setLangst] = useState<PublicSearchResult | null>(null);
  const [verhalen, setVerhalen] = useState<PublicSuccesverhaal[]>([]);
  // The animal in the donate block: whoever the site is leading with today.
  const doneerFoto = result?.items.find((d) => d.hoofdfotoUrl) ?? null;
  const [phase, setPhase] = useState<"loading" | "error" | "ready">("loading");

  // Its own request, and its own failure: the stories are a nice-to-have at the bottom of the page,
  // so a hiccup there must not take down the rows of animals above it.
  useEffect(() => {
    let active = true;
    void (async () => {
      try {
        const data = await fetchSuccesverhalen(KANAAL, 3);
        if (active) setVerhalen(data);
      } catch {
        if (active) setVerhalen([]);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let active = true;
    void (async () => {
      try {
        // Three rows, three questions of the same endpoint. Only the first decides the page state:
        // the other two are extra rows, and a row that comes back empty simply does not appear.
        const [nieuw, extra, wachtend] = await Promise.all([
          fetchSearch({ kanaal: KANAAL, size: 4, sortering: "nieuw" }),
          fetchSearch({ kanaal: KANAAL, size: 4, sortering: "nieuw", alleenBuitenbeentjes: true }),
          fetchSearch({ kanaal: KANAAL, size: 4, sortering: "langstzittend" }),
        ]);
        if (active) {
          setResult(nieuw);
          setBuitenbeentjes(extra);
          setLangst(wachtend);
          setPhase("ready");
        }
      } catch {
        if (active) setPhase("error");
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    document.title = "ikzoekbaas — ben jij mijn nieuwe baasje?";
  }, []);

  return (
    <>
      <div className="izb__breed">
        <section className="izb__hero">
          <div className="izb__hero-tekst">
            <h2 className="izb__hero-kop">Ik zoek baas</h2>
            <div className="izb__hero-kaart">
              <h1>Ben jij mijn nieuwe baasje?</h1>
            </div>
          </div>
          {/* The site's own hero image — a cut-out over a pale disc, not one of our animals. It is
              the same on every visit there, which is the point: the hero introduces the site, the
              rows below introduce the animals. */}
          <div className="izb__hero-beeld">
            <img src={heroKonijnen} alt="" aria-hidden="true" />
          </div>
        </section>

        <p className="izb__bekijk">Bekijk de dieren in de opvang</p>
        <div className="izb__tegels">
          {SOORT_GROEPEN.map((g) => {
            const telling = result?.groepen.find((t) => t.pad === g.pad);
            return (
              <button key={g.pad} type="button" className="izb__soorttegel" onClick={() => onZoek(g.pad)}>
                <span className="izb__soorttegel-glyph" aria-hidden="true">
                  <SoortGlyph pad={g.pad} />
                </span>
                <span className="izb__soorttegel-tekst">
                  <span className="izb__soorttegel-label">{g.label}</span>
                  <span className="izb__soorttegel-aantal">{telling ? telling.aantal : "—"}</span>
                </span>
                <span className="izb__soorttegel-pijl" aria-hidden="true">
                  <TegelPijl />
                </span>
              </button>
            );
          })}
        </div>

        <section className="izb__blok">
          <h2>Nieuw in het asiel</h2>
          <p className="izb__bloktekst">Deze dieren zijn onlangs geplaatst op de site.</p>
          {phase === "loading" && <p className="izb__staat">Bezig met laden…</p>}
          {phase === "error" && (
            <div className="izb__staat">
              <h2>Even niet bereikbaar</h2>
              <p>De dieren konden niet worden opgehaald. Probeer het zo nog eens.</p>
            </div>
          )}
          {phase === "ready" && result && result.items.length === 0 && (
            <p className="izb__staat">Er staan op dit moment geen dieren op de site.</p>
          )}
          {phase === "ready" && result && result.items.length > 0 && (
            <DierenRij dieren={result.items} onOpen={onOpen} />
          )}
        </section>

        {/* The donate block, in their place for it: after the first row of animals and before the
            buitenbeentjes. A white card with a round animal on the left and their lime block on the
            right, hanging out of the card at the top and the side.

            The button goes to the Dierenbescherming's PERMANENT donation page, not to the campaign
            URL their own button uses — that one is dated and will be gone next year, and a dead
            donate button is worse than a plain one. The campaign itself is behind "lees meer".

            The animal is the first one the site is showing today rather than a fixed illustration:
            we do not have their campaign cut-out, and one of ours is the honest stand-in. */}
        <section className="izb__doneerblok">
          <span className="izb__doneer-foto">
            <span className="izb__doneer-rond">
              <Photo src={doneerFoto?.hoofdfotoUrl ?? null} alt="" />
            </span>
          </span>
          <div className="izb__doneer-kaart">
            <h2>Jouw hulp is deze zomer onmisbaar</h2>
            <p>
              Tijdens de zomermaanden raken onze asielen vol met dieren die hulp nodig hebben. Ziek,
              verwaarloosd en achtergelaten komen ze binnen. Deze zomer vangen we ruim 2,5 keer
              zoveel dieren op dan in de rest van het jaar. Dat kunnen we alleen met jouw steun.
              Help vandaag nog en geef deze dieren de hulp die ze nodig hebben én verdienen.
            </p>
            <a
              className="izb__pil izb__pil--zwart izb__doneerknop"
              href={DONEER_URL}
              target="_blank"
              rel="noreferrer noopener"
            >
              Doneer nu
              <KnopPijl />
            </a>
            <a
              className="izb__doneerlink"
              href={ZOMERDRUKTE_URL}
              target="_blank"
              rel="noreferrer noopener"
            >
              <span>Lees meer over de zomerdrukte</span>
              <KnopPijl />
            </a>
          </div>
        </section>

        {/* Two more rows, each a real question of the same endpoint rather than a curated list — so
            they are always current and never need someone to maintain them. A row whose query comes
            back empty is left out entirely: an empty "buitenbeentjes" heading would read as broken,
            where its absence reads as "none right now". */}
        {phase === "ready" && buitenbeentjes && buitenbeentjes.items.length > 0 && (
          <section className="izb__blok">
            <h2>De buitenbeentjes</h2>
            <p className="izb__bloktekst">
              Alle dieren verdienen een nieuw thuis, maar sommige kunnen daar wat extra hulp bij
              gebruiken.
            </p>
            <DierenRij dieren={buitenbeentjes.items} onOpen={onOpen} />
          </section>
        )}

        {phase === "ready" && langst && langst.items.length > 0 && (
          <section className="izb__blok">
            <h2>Wachten het langst</h2>
            <p className="izb__bloktekst">
              Deze dieren zoeken al een tijd een baasje.
            </p>
            <DierenRij dieren={langst.items} onOpen={onOpen} />
          </section>
        )}

        {/* Their heading, and their split of it: the section is "Onlangs geplaatst" and the
            stories are what fills it. */}
        {verhalen.length > 0 && (
          <section className="izb__verhalenblok">
            <h2>Onlangs geplaatst</h2>
            <h3>Succesverhalen</h3>
            <div className="izb__verhalen">
              {verhalen.map((v) => (
                <VerhaalKaart key={v.platformId} verhaal={v} />
              ))}
            </div>
          </section>
        )}

        {/* Their closing block. The frame is a link, not an embed: their own page puts the video
            behind a cookie wall, and we have no consent to hand a third party — so this opens the
            wijzer rather than loading it. */}
        <section className="izb__wijzer">
          <a
            className="izb__wijzer-beeld"
            href={WIJZER_URL}
            target="_blank"
            rel="noreferrer noopener"
            aria-label="Dier &amp; Mens Wijzer"
          >
            <span className="izb__wijzer-frame">
              <span className="izb__wijzer-play" aria-hidden="true">
                <SpeelPijl />
              </span>
            </span>
          </a>
          <div className="izb__wijzer-tekst">
            <h2>Dier &amp; Mens Wijzer</h2>
            <p>
              Voordat je een dier in huis haalt, is het belangrijk om stil te staan bij een aantal
              praktische zaken. Weet je bijvoorbeeld wat het kost? Of hoeveel tijd er gaat zitten in
              de verzorging van een dier?
            </p>
            <a
              className="izb__pil izb__wijzerknop"
              href={WIJZER_URL}
              target="_blank"
              rel="noreferrer noopener"
            >
              Doe de wijzer
              <KnopPijl />
            </a>
            <a
              className="izb__doneerlink"
              href={TIPS_URL}
              target="_blank"
              rel="noreferrer noopener"
            >
              <span>Tips voor nieuwe baasjes</span>
            </a>
          </div>
        </section>
      </div>
    </>
  );
}

/**
 * Story 8-8 — de aanbodpagina van één opvang.
 *
 * <p>Een schil van niets: hij zoekt op wie deze sleutel is en zet daar het gewone zoekscherm omheen,
 * met die opvang als vaste grens. Een eigen scherm ernaast was een tweede kopie van dezelfde
 * zijbalk, facetten en paginering geweest — en die kopie had de filters niet, wat bij honderdtwintig
 * dieren het eerste is wat je mist.
 *
 * <p>Een sleutel die niet bestaat geeft een nette melding. Dat is geen zeldzaam geval: deze links
 * staan op de site van een asiel en blijven daar jaren staan.
 */
function OpvangPagina({ sleutel, insluiten, onOpen, onGroep, onQuery }: {
  sleutel: string;
  insluiten: boolean;
  onOpen: (groep: string, slug: string) => void;
  onGroep: (groep: string) => void;
  onQuery: (query: URLSearchParams) => void;
}) {
  const [opvang, setOpvang] = useState<PublicOpvang | null>(null);
  const [staat, setStaat] = useState<"laden" | "onbekend" | "fout" | "klaar">("laden");

  useEffect(() => {
    let actief = true;
    void (async () => {
      try {
        const alle = await fetchOpvangen(KANAAL);
        if (!actief) return;
        const gevonden = alle.find((o) => o.sleutel === sleutel) ?? null;
        setOpvang(gevonden);
        setStaat(gevonden ? "klaar" : "onbekend");
      } catch {
        if (actief) setStaat("fout");
      }
    })();
    return () => {
      actief = false;
    };
  }, [sleutel]);

  useEffect(() => {
    if (opvang?.naam) {
      document.title = `${opvang.naam} · ikzoekbaas`;
    }
  }, [opvang?.naam]);

  if (staat === "laden") {
    return <div className="izb__breed izb__breed--zoek"><p className="izb__staat">Bezig met laden…</p></div>;
  }
  if (staat === "fout") {
    return (
      <div className="izb__breed izb__breed--zoek">
        <div className="izb__staat">
          <h2>Even niet bereikbaar</h2>
          <p>De dieren konden niet worden opgehaald. Probeer het zo nog eens.</p>
        </div>
      </div>
    );
  }
  if (staat === "onbekend" || !opvang) {
    return (
      <div className="izb__breed izb__breed--zoek">
        <div className="izb__staat">
          <h2>Deze opvang kennen we niet</h2>
          <p>Het adres klopt niet, of deze opvang heeft op dit moment geen dieren op de site staan.</p>
        </div>
      </div>
    );
  }

  return (
    <Zoek
      groep=""
      opvang={opvang}
      insluiten={insluiten}
      onOpen={onOpen}
      onGroep={onGroep}
      onQuery={onQuery}
    />
  );
}

/**
 * One success story. No link and no button: the animal is gone, so there is nothing to click
 * through to — which is exactly what distinguishes this card from every other card on the page.
 */
function VerhaalKaart({ verhaal }: { verhaal: PublicSuccesverhaal }) {
  return (
    <article className="izb__verhaal">
      <span className="izb__verhaal-foto">
        <Photo src={verhaal.fotoUrl} alt={verhaal.fotoAlt ?? verhaal.naam} />
        <span className="izb__tegel-lint">Geplaatst</span>
      </span>
      <div className="izb__verhaal-blok">
        <h3>{verhaal.naam}</h3>
        {/* The shelter writes in paragraphs; keep them. */}
        {verhaal.verhaal.split(/\n{2,}/).map((alinea, i) => (
          <p key={i}>{alinea}</p>
        ))}
        {verhaal.opvangNaam && (
          <p className="izb__verhaal-opvang">
            <PinIcoon />
            {verhaal.opvangNaam}
          </p>
        )}
      </div>
    </article>
  );
}

/** One row of featured cards. */
function DierenRij({ dieren, onOpen }: {
  dieren: PublicSearchResult["items"];
  onOpen: (groep: string, slug: string) => void;
}) {
  // Four, the way their slider shows four on a wide screen and three below it — the CSS hides the
  // fourth at the narrower step. The row is an invitation; the search page is where all of them live.
  return (
    <div className="izb__rooster izb__rooster--uitgelicht">
      {dieren.slice(0, 4).map((dier) => (
        <UitgelichtKaart
          key={dier.platformId}
          dier={dier}
          onOpen={() => onOpen(groepVanSoort(dier.soort), dier.slug)}
        />
      ))}
    </div>
  );
}

/**
 * Which category page an animal belongs to, mirroring the platform's `SoortGroep`. Used only for
 * navigation from a card that did not come from a category page; the platform stays authoritative.
 */
function groepVanSoort(soort: string | null): string {
  const s = (soort ?? "").toLowerCase();
  if (s.includes("hond")) return "honden";
  if (s.includes("kat") || s.includes("poes")) return "katten";
  if (/konijn|cavia|hamster|rat|muis|gerbil|chinchilla|fret|knaagdier/.test(s)) {
    return "konijnen-en-knagers";
  }
  if (/vogel|parkiet|papegaai|kanarie|duif|agapornis/.test(s)) return "vogels";
  return "overige";
}

// ── search ───────────────────────────────────────────────────────────────────────────────────────

/** Read the filters back out of the address bar, so a shared or reloaded URL restores the search. */
function filtersFromUrl(groep: string): SearchFilters {
  const params = new URLSearchParams(globalThis.location?.search ?? "");
  const kenmerken: Record<string, string[]> = {};
  for (const entry of params.getAll("k")) {
    const split = entry.indexOf(":");
    if (split <= 0) continue;
    const sleutel = entry.slice(0, split);
    (kenmerken[sleutel] ??= []).push(entry.slice(split + 1));
  }
  return {
    kanaal: KANAAL,
    groep: groep || undefined,
    q: params.get("q") ?? undefined,
    alleenBeschikbaar: params.get("beschikbaar") === "true",
    alleenBuitenbeentjes: params.get("buitenbeentje") === "true",
    geslacht: params.getAll("geslacht"),
    leeftijd: params.getAll("leeftijd"),
    ras: params.getAll("ras"),
    opvang: params.getAll("opvang"),
    kenmerken,
    postcode: params.get("postcode") ?? undefined,
    straal: params.get("straal") ? Number(params.get("straal")) : undefined,
    sortering: (params.get("sorteer") as Sortering | null) ?? "nieuw",
    page: 0,
    size: PAGE_SIZE,
  };
}

function Zoek({ groep, opvang, insluiten, onOpen, onGroep, onQuery }: {
  groep: string;
  /**
   * Story 8-8 — de opvang waarbinnen gezocht wordt, of `null` voor de gewone zoekpagina.
   *
   * <p>Dit scherm doet allebei. Een eigen opvangscherm ernaast was een tweede kopie van dezelfde
   * zijbalk, dezelfde facetten, dezelfde paginering — en de eerste versie liet de filters weg met
   * het argument "wie hier komt is er al". Bij honderdtwintig dieren houdt dat argument geen stand:
   * dan wil je juist binnen dat asiel op soort, geslacht en leeftijd kunnen filteren.
   */
  opvang?: PublicOpvang | null;
  /** In een iframe op de site van het asiel: geen kruimelpad en geen kop. */
  insluiten?: boolean;
  onOpen: (groep: string, slug: string) => void;
  onGroep: (groep: string) => void;
  onQuery: (query: URLSearchParams) => void;
}) {
  const [filters, setFilters] = useState<SearchFilters>(() => ({
    ...filtersFromUrl(groep),
    opvangId: opvang?.sleutel,
  }));
  const [zoekterm, setZoekterm] = useState(() => filters.q ?? "");
  const [postcode, setPostcode] = useState(() => filters.postcode ?? "");
  const [result, setResult] = useState<PublicSearchResult | null>(null);
  const [phase, setPhase] = useState<"loading" | "error" | "ready">("loading");
  const [filtersOpen, setFiltersOpen] = useState(false);
  // Whether we are at the width where the sidebar folds away. Read from a media query rather than
  // guessed from the user agent, and kept in sync so a rotate or a resize does not strand the panel
  // hidden with no way to open it.
  const [smal, setSmal] = useState(
    () => globalThis.matchMedia?.("(max-width: 900px)").matches ?? false,
  );
  useEffect(() => {
    const mq = globalThis.matchMedia?.("(max-width: 900px)");
    if (!mq) {
      return;
    }
    const onChange = () => setSmal(mq.matches);
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, []);

  // Switching category is a different page, not a different filter: start clean rather than carrying
  // a dog-only breed filter onto the cat page, where it would silently return nothing.
  useEffect(() => {
    const fresh = filtersFromUrl(groep);
    setFilters({ ...fresh, opvangId: opvang?.sleutel });
    setZoekterm(fresh.q ?? "");
    setPostcode(fresh.postcode ?? "");
  }, [groep, opvang?.sleutel]);

  // Debounced so typing does not fire a request per keystroke.
  useEffect(() => {
    const id = setTimeout(
      () => setFilters((f) => ({ ...f, q: zoekterm || undefined, page: 0 })),
      250,
    );
    return () => clearTimeout(id);
  }, [zoekterm]);

  // De postcode wacht langer dan de zoekterm: hier hangt een opzoeking bij een externe dienst aan,
  // en "9723 CN" intypen zijn zes tussenstanden die geen van alle een postcode zijn. Een halve
  // seconde stilte is het signaal dat iemand klaar is met tikken.
  useEffect(() => {
    const id = setTimeout(
      () => setFilters((f) => ({ ...f, postcode: postcode.trim() || undefined, page: 0 })),
      500,
    );
    return () => clearTimeout(id);
  }, [postcode]);

  useEffect(() => {
    let active = true;
    setPhase("loading");
    void (async () => {
      try {
        const data = await fetchSearch(filters);
        if (active) {
          setResult(data);
          setPhase("ready");
        }
      } catch {
        if (active) setPhase("error");
      }
    })();
    return () => {
      active = false;
    };
  }, [filters]);

  // Mirror the filters into the address bar (kanaal and paging excluded — they are not the visitor's
  // choice and would only make a shared link longer).
  useEffect(() => {
    const { kanaal: _kanaal, groep: _groep, page: _page, size: _size, ...deelbaar } = filters;
    onQuery(searchParams(deelbaar));
  }, [filters, onQuery]);

  useEffect(() => {
    // Op de pagina van een opvang zet de schil de titel op haar naam; hier niet overheen schrijven.
    if (opvang) {
      return;
    }
    document.title = groep
      ? `${groepLabel(groep)} in het asiel · ikzoekbaas`
      : "Zoek een dier · ikzoekbaas";
  }, [groep, opvang]);

  const toggleFacet = useCallback((facet: string, waarde: string) => {
    setFilters((f) => {
      const vast = ["ras", "geslacht", "leeftijd", "opvang"] as const;
      if ((vast as readonly string[]).includes(facet)) {
        const key = facet as (typeof vast)[number];
        const huidig = f[key] ?? [];
        const next = huidig.includes(waarde)
          ? huidig.filter((v) => v !== waarde)
          : [...huidig, waarde];
        return { ...f, [key]: next, page: 0 };
      }
      const kenmerken = { ...(f.kenmerken ?? {}) };
      const huidig = kenmerken[facet] ?? [];
      const next = huidig.includes(waarde)
        ? huidig.filter((v) => v !== waarde)
        : [...huidig, waarde];
      if (next.length === 0) {
        delete kenmerken[facet];
      } else {
        kenmerken[facet] = next;
      }
      return { ...f, kenmerken, page: 0 };
    });
  }, []);

  const wissen = useCallback(() => {
    setZoekterm("");
    setFilters((f) => ({
      kanaal: KANAAL,
      groep: f.groep,
      sortering: f.sortering,
      page: 0,
      size: PAGE_SIZE,
    }));
  }, []);

  const actieveFilters =
    (filters.alleenBuitenbeentjes ? 1 : 0) +
    (filters.geslacht?.length ?? 0) +
    (filters.leeftijd?.length ?? 0) +
    (filters.ras?.length ?? 0) +
    (filters.opvang?.length ?? 0) +
    Object.values(filters.kenmerken ?? {}).reduce((n, v) => n + v.length, 0) +
    (filters.alleenBeschikbaar ? 1 : 0);

  const totaal = result?.totalElements ?? 0;
  const pagina = filters.page ?? 0;
  const paginas = Math.max(1, Math.ceil(totaal / PAGE_SIZE));

  return (
    <div className="izb__breed izb__breed--zoek">
      {!insluiten && (
        <div className="izb__kruimels">
          <span>Home</span>{" "}
          /{" "}
          <strong>
            {opvang
              ? (opvang.naam ?? "Opvang")
              : groep
                ? `${groepLabel(groep)} in het asiel`
                : "Alle dieren"}
          </strong>
        </div>
      )}

      {!insluiten && (
        <div className="izb__zoekkop-rij">
          <span className="izb__zoekkop-icoon" aria-hidden="true">
            {opvang ? <PawGlyph /> : <SoortGlyph pad={groep} />}
          </span>
          {opvang ? (
            <div>
              <h1 className="izb__zoekkop">{opvang.naam ?? "Aanbod"}</h1>
              {opvang.plaats && <p className="izb__opvang-plaats">{opvang.plaats}</p>}
            </div>
          ) : (
            <h1 className="izb__zoekkop">
              Zoeken naar {groep ? groepLabel(groep).toLowerCase() : "dieren"} in het asiel
            </h1>
          )}
        </div>
      )}

      <div className="izb__zoekbody">
        {/* On a phone sixteen open filter blocks above the results is not a page; the sidebar folds
            behind this and the results come first. */}
        <button
          type="button"
          className="izb__pil izb__filterknop"
          aria-expanded={filtersOpen}
          onClick={() => setFiltersOpen((o) => !o)}
        >
          {filtersOpen ? "Verberg filters" : `Filters${actieveFilters ? ` (${actieveFilters})` : ""}`}
        </button>

        <aside className="izb__filters-zij" aria-label="Filters" hidden={!filtersOpen && smal}>
          {/* Waar de bezoeker vandaan komt, bovenaan — dat is de eerste vraag die de echte site
              stelt, en terecht: een dier op 200 km is voor de meeste mensen geen dier.

              De twee horen bij elkaar en staan daarom in één veld: een postcode zonder afstand
              filtert niets (dan weet je alleen waar iemand woont) en een afstand zonder postcode
              kan nergens vandaan meten. "alle" is dus geen afstand maar het uitzetten ervan.

              Niet op de pagina van één opvang: die staat op één plek, dus een straal eromheen doet
              niets dan de lijst leegmaken zodra iemand ver weg woont. */}
          {!opvang && (
          <div className="izb__afstand">
            <input
              className="izb__afstand-invoer"
              type="text"
              inputMode="text"
              autoComplete="postal-code"
              value={postcode}
              onChange={(e) => setPostcode(e.target.value)}
              placeholder="Postcode of plaats"
              aria-label="Postcode of plaats"
            />
            <label className="izb__afstand-straal">
              <span className="izb__verborgen">Afstand</span>
              <select
                value={filters.straal ?? ""}
                onChange={(e) =>
                  setFilters((f) => ({
                    ...f,
                    straal: e.target.value ? Number(e.target.value) : undefined,
                    page: 0,
                  }))
                }
              >
                <option value="">alle</option>
                {STRALEN.map((km) => (
                  <option key={km} value={km}>{`+${km} km`}</option>
                ))}
              </select>
            </label>
          </div>
          )}

          <input
            className="izb__zoek"
            type="search"
            value={zoekterm}
            onChange={(e) => setZoekterm(e.target.value)}
            placeholder="Zoek op naam of ras"
            aria-label="Zoek op naam of ras"
          />

          <label className="izb__schakelaar">
            <input
              type="checkbox"
              checked={!!filters.alleenBeschikbaar}
              onChange={(e) =>
                setFilters((f) => ({ ...f, alleenBeschikbaar: e.target.checked, page: 0 }))
              }
            />
            Toon alleen beschikbare dieren
          </label>

          <label className="izb__schakelaar">
            <input
              type="checkbox"
              checked={!!filters.alleenBuitenbeentjes}
              onChange={(e) =>
                setFilters((f) => ({ ...f, alleenBuitenbeentjes: e.target.checked, page: 0 }))
              }
            />
            Alleen buitenbeentjes
          </label>

          <div className="izb__filterblok">
            <button type="button" className="izb__filterblok-kop" aria-expanded="true" disabled>
              Diersoort
              <FilterChevron />
            </button>
            <ul className="izb__opties">
              {SOORT_GROEPEN.map((g) => {
                const telling = result?.groepen.find((t) => t.pad === g.pad);
                return (
                  <li key={g.pad}>
                    <button
                      type="button"
                      className={g.pad === groep ? "izb__optie is-gekozen" : "izb__optie"}
                      onClick={() => onGroep(g.pad === groep ? "" : g.pad)}
                    >
                      <span>{g.label}</span>
                      <span className="izb__aantal">({telling?.aantal ?? 0})</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>

          {actieveFilters > 0 && (
            <button type="button" className="izb__wissen" onClick={wissen}>
              Wis alle filters ({actieveFilters})
            </button>
          )}

          {/* Het opvangfacet valt weg zodra je al ín een opvang zit: één optie met het volledige
              aantal erachter is geen filter maar een herhaling van de kop. */}
          {(result?.facetten ?? [])
            .filter((facet) => !opvang || facet.sleutel !== "opvang")
            .map((facet) => (
              <FacetBlok key={facet.sleutel} facet={facet} onToggle={toggleFacet} />
            ))}
        </aside>

        <main className="izb__resultaten">
          <div className="izb__resultaatkop">
            <p className="izb__telling">
              {phase === "ready"
                ? `${totaal} ${totaal === 1 ? "dier" : "dieren"} in het asiel`
                : " "}
            </p>
            <label className="izb__sorteer">
              Sorteer op:
              <select
                value={filters.sortering ?? "nieuw"}
                onChange={(e) =>
                  setFilters((f) => ({ ...f, sortering: e.target.value as Sortering, page: 0 }))
                }
              >
                <option value="nieuw">Nieuw, aflopend</option>
                <option value="langstzittend">Langstzittend, oplopend</option>
              </select>
            </label>
          </div>

          {phase === "loading" && <p className="izb__staat">Bezig met laden…</p>}
          {phase === "error" && (
            <div className="izb__staat">
              <h2>Even niet bereikbaar</h2>
              <p>De dieren konden niet worden opgehaald. Probeer het zo nog eens.</p>
            </div>
          )}
          {phase === "ready" && totaal === 0 && (
            <div className="izb__staat">
              <h2>Geen dieren gevonden</h2>
              <p>Pas je filters aan of bekijk een andere diersoort.</p>
            </div>
          )}

          {phase === "ready" && totaal > 0 && (
            <div className="izb__rooster">
              {(result?.items ?? []).map((dier) => (
                <DierTegel
                  key={dier.platformId}
                  dier={dier}
                  onOpen={() => onOpen(groep || groepVanSoort(dier.soort), dier.slug)}
                />
              ))}
            </div>
          )}

        </main>

        {/* Outside the results column on purpose: their pagination is centred on the page, not
            under the cards, so it spans both grid columns here. */}
        {phase === "ready" && paginas > 1 && (
          <nav className="izb__paginering" aria-label="Paginering">
            <button
              type="button"
              disabled={pagina === 0}
              aria-label="Vorige pagina"
              onClick={() => setFilters((f) => ({ ...f, page: Math.max(0, pagina - 1) }))}
            >
              <SliderPijl richting="links" />
            </button>
            {paginaNummers(pagina, paginas).map((n, i) =>
              n === null ? (
                <span key={`gap${i}`} className="izb__paginering-punten" aria-hidden="true">
                  …
                </span>
              ) : (
                <button
                  key={n}
                  type="button"
                  className={n === pagina ? "is-nu" : undefined}
                  aria-current={n === pagina ? "page" : undefined}
                  aria-label={`Pagina ${n + 1}`}
                  onClick={() => setFilters((f) => ({ ...f, page: n }))}
                >
                  {n + 1}
                </button>
              ),
            )}
            <button
              type="button"
              disabled={pagina + 1 >= paginas}
              aria-label="Volgende pagina"
              onClick={() => setFilters((f) => ({ ...f, page: pagina + 1 }))}
            >
              <SliderPijl richting="rechts" />
            </button>
          </nav>
        )}
      </div>
    </div>
  );
}

/**
 * The page numbers to show: always the first and last, a window around the current one, and `null`
 * where a gap is elided. Keeps the row a fixed width whether there are 3 pages or 300 — the real
 * site shows "1 2 3 4 …" rather than a hundred buttons.
 */
export function paginaNummers(huidig: number, totaal: number): Array<number | null> {
  if (totaal <= 7) {
    return Array.from({ length: totaal }, (_, i) => i);
  }
  const uit = new Set<number>([0, totaal - 1, huidig]);
  for (const n of [huidig - 1, huidig + 1]) {
    if (n > 0 && n < totaal - 1) {
      uit.add(n);
    }
  }
  const gesorteerd = [...uit].sort((a, b) => a - b);
  const rij: Array<number | null> = [];
  let vorige: number | null = null;
  for (const n of gesorteerd) {
    if (vorige !== null && n - vorige > 1) {
      rij.push(null);
    }
    rij.push(n);
    vorige = n;
  }
  return rij;
}

/**
 * One filter block: a heading you fold open, then the options. Long lists show the first six with a
 * "toon alle" underneath — sixteen questions each listing every answer would bury the results.
 *
 * <p>A block with a selection in it opens on arrival, so a shared URL shows what it filtered on
 * rather than a row of collapsed headings the visitor has to hunt through.
 */
function FacetBlok({ facet, onToggle }: {
  facet: Facet;
  onToggle: (facet: string, waarde: string) => void;
}) {
  const heeftKeuze = facet.opties.some((o) => o.gekozen);
  const [open, setOpen] = useState(heeftKeuze);
  const [alles, setAlles] = useState(false);
  const zichtbaar = alles ? facet.opties : facet.opties.slice(0, 6);
  return (
    <div className="izb__filterblok">
      <button
        type="button"
        className="izb__filterblok-kop"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        {facet.label}
        <FilterChevron />
      </button>
      {open && (
        <>
          <ul className="izb__opties">
            {zichtbaar.map((optie) => (
              <li key={optie.waarde}>
                <label className={optie.gekozen ? "izb__optie is-gekozen" : "izb__optie"}>
                  <input
                    type="checkbox"
                    checked={optie.gekozen}
                    onChange={() => onToggle(facet.sleutel, optie.waarde)}
                  />
                  <span>{optie.label || optie.waarde}</span>
                  <span className="izb__aantal">({optie.aantal})</span>
                </label>
              </li>
            ))}
          </ul>
          {facet.opties.length > 6 && (
            <button type="button" className="izb__meeropties" onClick={() => setAlles((a) => !a)}>
              {alles ? "Toon minder" : `Toon alle ${facet.opties.length}`}
            </button>
          )}
        </>
      )}
    </div>
  );
}

/**
 * One result card. The photo IS the card — no border, no shadow, no box: a turquoise ribbon rides
 * the top-left corner, and the name and facts sit underneath on the page's own white.
 *
 * <p>The ribbon says what the site says: an animal you can still meet reads "Nieuw in het asiel",
 * and only a departure from that ("Gereserveerd", "Nog niet beschikbaar") is spelled out. The photo
 * never appears without a ribbon — a blank one would read as a rendering fault rather than as
 * "nothing to say".
 */
/**
 * The card the home page uses for its featured row — a different animal from the search result
 * card, and deliberately so on the real site: it is taller, its facts sit in a mint block under the
 * photo instead of on the page, and it carries the intro text and a "bekijk het profiel" affordance.
 * That is what makes the home row read as an invitation rather than as a list.
 */
function UitgelichtKaart({ dier, onOpen }: {
  dier: PublicSearchResult["items"][number];
  onOpen: () => void;
}) {
  const geboren = nlDatum(dier.geboortedatum);
  return (
    <button type="button" className="izb__uitgelicht" onClick={onOpen}>
      <span className="izb__uitgelicht-foto">
        <Photo src={dier.hoofdfotoUrl} alt={dier.hoofdfotoAlt ?? dier.naam} />
        <span className="izb__tegel-lint">{lintTekst(dier.beschikbaarheid)}</span>
      </span>
      <span className="izb__uitgelicht-blok">
        <span className="izb__uitgelicht-feiten">
          {dier.geslacht && (
            <span className="izb__uitgelicht-feit">
              {dier.geslacht}
              <GeslachtIcoon geslacht={dier.geslacht} />
            </span>
          )}
          {geboren && <span className="izb__uitgelicht-feit">{geboren}</span>}
        </span>
        <h3 className="izb__uitgelicht-naam">{dier.naam}</h3>
        {dier.intro && <p className="izb__uitgelicht-intro">{dier.intro}</p>}
        <span className="izb__uitgelicht-link">
          Bekijk het profiel
          <TegelPijl />
        </span>
      </span>
    </button>
  );
}

/**
  * Vergeven: gereserveerd, geplaatst of nog niet beschikbaar. De kaart blijft staan — een reservering
  * kan afketsen — maar houdt zich op de achtergrond. Geen antwoord betekent beschikbaar: dan heeft de
  * opvang niets anders gezegd. Dezelfde regel als waarop de API ze onderaan zet.
  */
export function isVergeven(beschikbaarheid: string | null): boolean {
  const b = (beschikbaarheid ?? "").trim().toLowerCase();
  return b !== "" && b !== "beschikbaar";
}

export function lintTekst(beschikbaarheid: string | null): string {
  if (!beschikbaarheid || beschikbaarheid.trim().toLowerCase() === "beschikbaar") {
    return "Nieuw in het asiel";
  }
  return beschikbaarheid;
}

function DierTegel({ dier, onOpen }: {
  dier: PublicSearchResult["items"][number];
  onOpen: () => void;
}) {
  // Gender and birth date, the way the site words it: "Vrouwelijk • 1 november 2015".
  const feiten = [dier.geslacht, nlDatum(dier.geboortedatum) ?? dier.leeftijd]
    .filter(Boolean)
    .join(" • ");
  // De plaats wint van de opvangnaam: "Born" zegt een bezoeker waar het dier zit, waar
  // "Dierenbeschermingscentrum Limburg" dat pas zegt als je de opvang al kent. De echte site zet er
  // ook de plaats neer. Zonder leesbaar adres valt hij terug op de naam.
  const plaats = dier.plaats ?? dier.locatie ?? dier.opvangNaam;
  return (
    <button
      type="button"
      className={"izb__tegel" + (isVergeven(dier.beschikbaarheid) ? " izb__tegel--vergeven" : "")}
      onClick={onOpen}
    >
      <span className="izb__tegel-foto">
        <Photo src={dier.hoofdfotoUrl} alt={dier.hoofdfotoAlt ?? dier.naam} />
        <span className="izb__tegel-lint">{lintTekst(dier.beschikbaarheid)}</span>
      </span>
      <span className="izb__tegel-tekst">
        <h3 className="izb__tegel-naam">{dier.naam}</h3>
        {feiten && <p className="izb__tegel-regel">{feiten}</p>}
        {plaats && (
          <p className="izb__tegel-plaats">
            <PinIcoon />
            {plaats}
          </p>
        )}
      </span>
    </button>
  );
}

// ── detail ───────────────────────────────────────────────────────────────────────────────────────

/** One accordion step: the header with its glyph and chevron, and the content that unfolds below. */
function Stap({ nr, titel, open, onToggle, children }: {
  nr: number;
  titel: string;
  open: boolean;
  onToggle: () => void;
  children: ReactNode;
}) {
  const dp = titel.indexOf(":");
  return (
    <section className={open ? "izb__stap is-open" : "izb__stap"}>
      <button type="button" className="izb__stap-kop" aria-expanded={open} onClick={onToggle}>
        <StapIcoon nr={nr} />
        <span className="izb__stap-titel">
          <b>{dp === -1 ? titel : titel.slice(0, dp + 1)}</b>
          {dp === -1 ? "" : titel.slice(dp + 1)}
        </span>
        <StapChevron open={open} />
      </button>
      {open && <div className="izb__stap-inhoud" data-stap={nr}>{children}</div>}
    </section>
  );
}

function Detail({
  slug,
  groep,
  onBack,
  onHome,
}: {
  slug: string;
  groep: string;
  onBack: () => void;
  onHome: () => void;
}) {
  const [dier, setDier] = useState<PublicAnimalDetail | null>(null);
  const [phase, setPhase] = useState<"loading" | "missing" | "error" | "ready">("loading");
  const [foto, setFoto] = useState(0);
  // All three may stand open at once; step 1 opens on arrival because that is where the story is.
  const [open, setOpen] = useState<number[]>([1]);
  const wissel = (nr: number) =>
    setOpen((cur) => (cur.includes(nr) ? cur.filter((n) => n !== nr) : [...cur, nr]));

  useEffect(() => {
    let active = true;
    setPhase("loading");
    void (async () => {
      try {
        const result = await fetchPublication(slug, KANAAL);
        if (active) {
          setDier(result);
          setFoto(0);
          setPhase("ready");
        }
      } catch (error) {
        if (active) {
          setPhase(statusOf(error) === 404 ? "missing" : "error");
        }
      }
    })();
    return () => {
      active = false;
    };
  }, [slug]);

  // Tellen gebeurt in useTelWeergave: pagina zichtbaar, vijf seconden, één keer per bezoeker per
  // dag. Dat stond hier, en staat nu naast DierMatch die precies dezelfde zeven nodig heeft.
  useTelWeergave(dier?.slug, "IKZOEKBAAS");

  useEffect(() => {
    document.title = dier ? `${dier.naam} · ikzoekbaas` : "ikzoekbaas";
  }, [dier]);

  if (phase === "loading") {
    return <p className="izb__staat">Bezig met laden…</p>;
  }
  if (phase === "missing" || phase === "error") {
    return (
      <div className="izb__staat">
        <h2>{phase === "missing" ? "Dit dier staat er niet (meer)" : "Even niet bereikbaar"}</h2>
        <p>
          {phase === "missing"
            ? "Misschien heeft dit dier al een nieuw thuis gevonden."
            : "De pagina kon niet worden opgehaald. Probeer het zo nog eens."}
        </p>
        <button type="button" className="izb__knop" onClick={onBack}>
          Terug naar alle dieren
        </button>
      </div>
    );
  }
  if (!dier) {
    return null;
  }

  const naam = dier.naam || "—";
  const geboortedatum = nlDatum(dier.geboortedatum);
  const fotos = dier.fotos ?? [];
  const huidige = fotos.length > 0 ? fotos[Math.min(foto, fotos.length - 1)] : null;

  // The fixed facts head the traits table on ikzoekbaas, before the answered questions.
  const vasteRijen = [
    { sleutel: "_soort", label: "Soort", waarde: dier.soort ?? "" },
    { sleutel: "_ras", label: "Ras", waarde: dier.ras ?? "" },
    { sleutel: "_geslacht", label: "Geslacht", waarde: dier.geslacht ?? "" },
    { sleutel: "_geboortedatum", label: "Geboortedatum", waarde: geboortedatum ?? "" },
  ].filter((r) => r.waarde !== "");

  // The answered questions, in the order the platform stored them (catalogue order). A publication
  // that only carried trait KEYS still gets a table: the key becomes the label and "Ja" the answer,
  // which is what a confirmed trait means.
  const antwoordRijen =
    dier.kenmerkAntwoorden.length > 0
      ? dier.kenmerkAntwoorden.map((k) => ({
          sleutel: k.sleutel,
          label: k.label || kenmerkLabel(k.sleutel),
          waarde: k.antwoord,
        }))
      : dier.kenmerken.map((sleutel) => ({
          sleutel,
          label: kenmerkLabel(sleutel),
          waarde: "Ja",
        }));

  const rijen = [...vasteRijen, ...antwoordRijen];

  const teksten = (
    [
      ["Over dit dier", dier.overDitDier],
      ["Past bij", dier.pastBij],
      ["Belangrijk om te weten", dier.belangrijk],
    ] as Array<[string, string | null]>
  ).filter((r): r is [string, string] => !!r[1] && r[1].trim() !== "");

  const contact = dier.contact;
  const heeftContactRegels = !!(
    dier.locatie ||
    contact?.adres ||
    contact?.telefoon ||
    contact?.email
  );
  const asielNaam = dier.locatie ?? contact?.naam ?? dier.opvangNaam;
  // Wat er gedeeld wordt: precies de pagina waar de bezoeker staat.
  const deelUrl = globalThis.location?.href ?? "";

  return (
    <div className="izb__breed izb__breed--detail">
      {/* Their trail is three deep — Home / soort / naam — and the first crumb is what gets you
          off a detail page without the back button. */}
      <div className="izb__kruimels">
        <button type="button" onClick={onHome}>
          Home
        </button>{" "}
        /{" "}
        <button type="button" onClick={onBack}>
          {groep ? groepLabel(groep) : "Alle dieren"}
        </button>{" "}
        / <strong>{naam}</strong>
      </div>

      <div className="izb__body">
        <aside className="izb__zij">
          <div className="izb__profiel">
            <span className="izb__rondfoto">
              <Photo src={huidige?.url ?? null} alt={huidige?.alt ?? naam} />
            </span>
            {dier.beschikbaarheid && <span className="izb__badge">{dier.beschikbaarheid}</span>}
            <h2 className="izb__naam">{naam}</h2>
            {geboortedatum && (
              <p className="izb__geboorte">
                <TaartIcoon /> {geboortedatum}
              </p>
            )}
          </div>

          {(asielNaam || contact?.adres) && (
            <div className="izb__kaart">
              <h3>{asielNaam ?? ""}</h3>
              {contact?.adres && <p className="izb__adres">{contact.adres}</p>}
              {contact?.url && (
                <a className="izb__knop" href={contact.url} rel="noreferrer noopener nofollow">
                  Andere dieren op deze locatie
                </a>
              )}
            </div>
          )}

          <div className="izb__kaart izb__kaart--deel">
            <h3>Deel profiel van {naam}</h3>
            <p>Ik zoek een baas! Vertel het door aan iemand die op zoek is naar een dier zoals {naam}.</p>
            {/* Vier echte knoppen in plaats van vier letters. De links zijn die van hun eigen
                deelblok: de deelbare URL is deze pagina, dus `location.href` — en niet iets wat wij
                hier in elkaar zetten, want dan klopt hij niet op de padingang. */}
            <span className="izb__socials">
              {DELEN.map(({ merk, label, url }) => (
                <a
                  key={merk}
                  href={url(deelUrl, naam)}
                  target="_blank"
                  rel="noreferrer noopener"
                  aria-label={label}
                  title={label}
                >
                  <DeelIcoon merk={merk} />
                </a>
              ))}
            </span>
          </div>
        </aside>

        <main className="izb__hoofd">
          <div className="izb__beeld">
            <div className="izb__hero-foto">
              <Photo src={huidige?.url ?? null} alt={huidige?.alt ?? naam} />
            </div>
            {fotos.length > 1 && (
              <p className="izb__teller">
                <button
                  type="button"
                  onClick={() => setFoto((i) => (i - 1 + fotos.length) % fotos.length)}
                  aria-label="Vorige foto"
                >
                  <SliderPijl richting="links" />
                </button>
                {`${Math.min(foto, fotos.length - 1) + 1}/${fotos.length}`}
                <button
                  type="button"
                  onClick={() => setFoto((i) => (i + 1) % fotos.length)}
                  aria-label="Volgende foto"
                >
                  <SliderPijl richting="rechts" />
                </button>
              </p>
            )}
          </div>

          <div className="izb__stappen">
            <Stap
              nr={1}
              titel="Stap 1: Ontdek of ik bij je pas"
              open={open.includes(1)}
              onToggle={() => wissel(1)}
            >
              {teksten.length === 0 && rijen.length === 0 && (
                <p className="izb__leegtekst">Er is nog geen beschrijving van dit dier.</p>
              )}
              {/* The site puts no sub-headings above the text blocks: the story runs as one piece,
                  with the traits table beneath it. */}
              {teksten.map(([kop, tekst]) => (
                <section key={kop} className="izb__tekst">
                  {tekst.split(/\n{2,}/).map((alinea, i) => (
                    <p key={i}>{alinea}</p>
                  ))}
                </section>
              ))}
              {rijen.length > 0 && (
                <section className="izb__kenmerken">
                  <h3>Kenmerken</h3>
                  <dl>
                    {rijen.map((r) => (
                      <div key={r.sleutel} className="izb__kenmerk">
                        <dt>{r.label}</dt>
                        <dd>
                          <span aria-hidden="true">:</span>
                          {r.waarde}
                        </dd>
                      </div>
                    ))}
                  </dl>
                </section>
              )}
            </Stap>

            <Stap
              nr={2}
              titel="Stap 2: Ken onze tarieven"
              open={open.includes(2)}
              onToggle={() => wissel(2)}
            >
              {/* Step 2 is only a pointer on the site: the rates live on the rates page, not in the
                  advertisement. */}
              <span className="izb__knop izb__knop--groot">
                Naar de tarieven
                <KnopPijl />
              </span>
            </Stap>

            <Stap
              nr={3}
              titel="Stap 3: Neem contact op"
              open={open.includes(3)}
              onToggle={() => wissel(3)}
            >
              <p>
                {contact?.tekst ||
                  `Wil je ${naam} leren kennen? Neem contact op met het asiel; zij vertellen je hoe een kennismaking werkt.`}
              </p>
              {heeftContactRegels && (
                <p className="izb__contact">
                  {dier.locatie && <strong>{dier.locatie}</strong>}
                  {contact?.adres && <span>{contact.adres}</span>}
                  {contact?.telefoon && <span>{contact.telefoon}</span>}
                  {contact?.email && (
                    <a className="izb__mail" href={`mailto:${contact.email}`}>
                      {contact.email}
                    </a>
                  )}
                </p>
              )}
              {contact?.email && (
                <a className="izb__knop izb__knop--groot" href={`mailto:${contact.email}`}>
                  <KnopEnvelop />
                  Neem contact op
                </a>
              )}
            </Stap>
          </div>
        </main>
      </div>
    </div>
  );
}
